import { DollarSign, Gift } from 'lucide-preact';

import { AvailableCoupons } from '@/components/AvailableCoupons';
import { Redemptions } from '@/components/Redemptions';
import { useCustomerLoyalty } from '@/context/LoyaltyContext';
import { useNavigation } from '@/context/NavigationContext';
import { useWidgetConfig } from '@/context/WidgetConfigContext';
import { COLORS } from '@/helpers/constants/theme';
import { formatNumberWithCommas } from '@/helpers/functions/number';
import { lightenColor } from '@/helpers/functions/theme';
import type { I_AccordionItemProps } from '@/types/AccordionItem';
import type { I_EnableLoyaltyPointsInfo, T_CouponsInfo } from '@/types/RegisteredCustomer';
import { E_AppRoutes } from '@/types/Routes';

import { useHoverBackground } from './useHoverBackground';

export const usePointsAccordionItems = (
  loyaltyPoints?: I_EnableLoyaltyPointsInfo,
  flexiblePointsEnabled = true,
  coupons?: T_CouponsInfo,
): I_AccordionItemProps[] => {
  const commonPointsConfig = loyaltyPoints ?? coupons;
  if (!commonPointsConfig) return [];

  const { availablePoints, exchangeRate, currency, pointsBalanceTitleEn } = commonPointsConfig;

  const loyaltyPointsRedemptions = loyaltyPoints?.redemptions;
  const couponsRedemptions = coupons?.redemptions;
  const { navigateTo } = useNavigation();
  const { setPointsToRedeem } = useCustomerLoyalty();

  const { theme, fullRedemptionOnly } = useWidgetConfig();

  const onFlexiblePointsClick = async () => {
    if (fullRedemptionOnly) {
      setPointsToRedeem(availablePoints);
      navigateTo(E_AppRoutes.REDEMPTION);
    } else {
      navigateTo(E_AppRoutes.FLEXIBLE_POINTS);
    }
  };
  const iconColor = theme?.headingColor;
  const lighterBackground = lightenColor(iconColor ?? '#FFF7ED', 90);

  const { backgroundColor = COLORS.BRAND_PRIMARY, textColor = COLORS.TEXT_INVERSE } =
    theme?.primaryButton ?? {};

  const { backgroundColor: hoverBackground, hoverHandlers } = useHoverBackground({
    baseColor: backgroundColor,
  });

  return [
    {
      value: 'points',
      triggerContent: (
        <div className='flex items-center justify-center space-x-3'>
          <div
            className='flex min-h-10 min-w-10 items-center justify-center rounded-full'
            style={{ backgroundColor: lighterBackground }}
          >
            <Gift className='min-h- min-w-1 p-1' style={{ color: iconColor }} />
          </div>
          <div className='text-left'>
            <h3 className='font-semibold'>
              You have {formatNumberWithCommas(availablePoints)} {pointsBalanceTitleEn}
            </h3>
            <h5>Redeem for exclusive rewards</h5>
          </div>
        </div>
      ),
      children: (
        <div className='space-y-4'>
          {flexiblePointsEnabled && (
            <>
              <div
                className='rounded-lg border bg-gradient-to-r p-4'
                style={{
                  borderColor: lighterBackground,
                  backgroundColor: lighterBackground,
                }}
              >
                <div className='mb-3 flex items-center justify-between'>
                  <div className='flex items-center space-x-3'>
                    <div
                      className='flex h-10 w-10 items-center justify-center rounded-full'
                      style={{ backgroundColor: lightenColor(iconColor ?? '#FFF7ED', 80) }}
                    >
                      <DollarSign className='h-5 w-5' style={{ color: iconColor }} />
                    </div>

                    <div>
                      <h4>
                        {fullRedemptionOnly
                          ? `Redeem Your ${pointsBalanceTitleEn}`
                          : 'Flexible Redemption'}
                      </h4>
                      <h5>
                        1 {pointsBalanceTitleEn?.toLocaleLowerCase()} ={' '}
                        {(1 / exchangeRate)?.toFixed(2)} {currency}
                      </h5>
                    </div>
                  </div>
                </div>
                <button
                  onClick={onFlexiblePointsClick}
                  {...hoverHandlers}
                  className='w-full rounded-lg px-4 py-2 font-medium transition-colors'
                  style={{ backgroundColor: hoverBackground, color: textColor }}
                >
                  Redeem {pointsBalanceTitleEn} for Cash
                </button>
              </div>
              <Redemptions redemptions={loyaltyPointsRedemptions} />
            </>
          )}
          {coupons && (
            <>
              <AvailableCoupons coupons={coupons} />
              <Redemptions redemptions={couponsRedemptions} />
            </>
          )}
        </div>
      ),
    },
  ];
};
